import { Instance, SnapshotOut, types, flow, getRoot } from "mobx-state-tree"
import { RootStoreModel } from "~models/root-store"
import { User } from "~models/user"
import { withEnvironment } from "../extensions/with-environment"
import { OrderModel, OrderSnapshot } from "~models/order/order"
import {
  AttachTakeOrderData,
  ContactData,
  Event,
  EventNames,
  FLAG_REPLY,
  FriendCommonData,
  PendingOrderData,
  TakeOrderData,
} from "~proto"
/**
 * 錢包資訊 store
 */

// "id": 1,
// "username": "dev01",
// "is_phone_verify": true,
// "is_id_verify": true,
// "name": "莉莉",
// "gender": 0,
// "credit_score": 600,
// "avatar": "https://img2.woyaogexing.com/2021/09/26/b1374554a6b14b30be62140c91edc97b!400x400.jpeg"
export const OrderStoreModel = types
  .model("OrderStore")
  .props({
    orders: types.optional(types.map(OrderModel), {}),
  })
  .volatile<{
    tradingRoomCreatedData: AttachTakeOrderData | false
    isTradingRoomAlertShowing: boolean
  }>(() => ({
    tradingRoomCreatedData: false,
    isTradingRoomAlertShowing: false,
  }))
  .extend(withEnvironment)
  .views((self) => ({
    get ol() {
      return Array.from(self.orders.values()).sort((a, b) => {
        return a.created > b.created ? -1 : 1
      })
    },
    get orderList() {
      return this.ol.filter((order) => order.stock >= 100)
    },
    get myOrderList() {
      const { userStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      return this.orderList.filter(({ user_id }) => user_id === userStore.currentUser.id)
    },
  }))
  .actions((self) => {
    const { apiStore, authStore } = getRoot<Instance<typeof RootStoreModel>>(self)
    const adaptor = (data: OrderSnapshot) => {
      return {
        ...data,
        created: new Date(data.created).valueOf(),
        amount: Number(data.amount),
      }
    }
    // create order
    const handleOrderCreate = (data: OrderSnapshot) => {
      data.avatar = data.avatar || ""
      return self.orders.put(adaptor(data))
    }
    const onOrderCreated = ({ data }: Event<OrderSnapshot>) => {
      return handleOrderCreate(data)
    }
    // update order
    const handleOrderUpdate = (data: OrderSnapshot) => {
      const order = self.orders.get(data.order_no)
      if (order) {
        order.update(adaptor(data))
      }
      return order
    }
    const onOrderUpdated = ({ data }: Event<OrderSnapshot>) => {
      return handleOrderUpdate(data)
    }
    // delete order
    const handleOrderDelete = (data: Partial<OrderSnapshot>) => {
      return self.orders.delete(data.order_no)
    }
    const onOrderDeleted = ({ data }: Event<Partial<OrderSnapshot>>) => {
      return handleOrderDelete(data)
    }

    const setIsTradingRoomAlertShowing = (v: boolean) => {
      self.isTradingRoomAlertShowing = v
    }
    const setTradingRoomCreatedData = (data: AttachTakeOrderData) => {
      self.tradingRoomCreatedData = data
    }
    const handleTradingRoomCreated = (data: AttachTakeOrderData) => {
      setTradingRoomCreatedData(data)
    }
    const onTradingRoomCreated = ({ data }: Event<AttachTakeOrderData>) => {
      return handleTradingRoomCreated(data)
    }
    const setOrderList = (data) => {
      data.forEach(handleOrderCreate)
    }
    const loadOrderlist = flow(function* () {
      const token = authStore.getToken
      try {
        const res = yield apiStore.getPendingOrders({ token })
        self.orders.clear()
        setOrderList(res)
      } catch (e) {
        // console.warn(e.message)
      }
    })
    const loadMyOrders = async (isSettled) => {
      const token = authStore.getToken
      try {
        const res = await apiStore.getMyOrders({ token, isSettled })
        return res
      } catch (e) {
        // console.warn(e.message)
      }
    }
    const loadMyOrderDetail = async (id: string | number) => {
      const token = authStore.getToken
      try {
        const res = await apiStore.getMyOrderDetail({ token, id })
        return res
      } catch (e) {
        // console.warn(e.message)
      }
    }
    const publicPendingOrderNew = flow(function* (data: PendingOrderData, onData, onError) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_NEW, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    })
    const publicPendingUpdated = flow(function* (data: PendingOrderData, onData, onError) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_UPDATED, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    })
    const publicPendingOrderCancel = flow(function* (data: PendingOrderData, onData, onError) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_CANCEL, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    })
    const publicPendingOrderDetails = flow(function* (data: PendingOrderData, onData, onError) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<PendingOrderData>(EventNames.PENDING_ORDER_DETAILS, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    })
    const publicTakeOrderNew = flow(function* (data: TakeOrderData, onData, onError) {
      const { eventStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const evt = new Event<TakeOrderData>(EventNames.TAKE_ORDER_NEW, data)
      eventStore.publish(evt, {
        flags: FLAG_REPLY,
        onData,
        onError,
      })
    })
    const onContactCs = flow(function* (onData: (v: User) => void, onError: (v: Error) => void) {
      const { eventStore, contactStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const { cs_account } = yield apiStore.getForceCs({
        token: authStore.getToken,
      })
      const csContact = contactStore.byUsername(cs_account)
      if (!csContact) {
        const evt = new Event<FriendCommonData>(EventNames.FRIEND_ADD, {
          username: cs_account,
        })
        eventStore.publish(evt, {
          flags: FLAG_REPLY,
          onData: (data: ContactData) => {
            const { user } = contactStore.onContactCreatedHandler(data)
            setTimeout(() => {
              onData(user)
            })
          },
          onError,
        })
      } else {
        setTimeout(() => {
          onData(csContact.user)
        })
      }
    })
    const onContact = flow(function* (account: string, onData: (v: User) => void) {
      const { chatStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const contact = chatStore.byUsername(account)
      setTimeout(() => {
        onData(contact)
      })
    })
    return {
      onContact,
      onContactCs,
      onOrderCreated,
      onOrderUpdated,
      onOrderDeleted,
      onTradingRoomCreated,
      setIsTradingRoomAlertShowing,
      setTradingRoomCreatedData,
      handleOrderCreate,
      handleOrderUpdate,
      handleOrderDelete,
      handleTradingRoomCreated,
      loadOrderlist,
      loadMyOrders,
      loadMyOrderDetail,
      publicPendingOrderNew,
      publicPendingUpdated,
      publicPendingOrderCancel,
      publicPendingOrderDetails,
      publicTakeOrderNew,
    }
  })

type OrderStoreType = Instance<typeof OrderStoreModel>
export interface OrderStore extends OrderStoreType {}
type OrderStoreSnapshotType = SnapshotOut<typeof OrderStoreModel>
export interface OrderStoreSnapshot extends OrderStoreSnapshotType {}
export const createOrderStoreDefaultModel = () => types.optional(OrderStoreModel, {})
